// Helpers for picking readable text over user-chosen surface colors (asset grid, org branding).
// The app theme controls antd tokens; these cover colors that are data, not theme.

const parseHexColor = hex => {
    if(typeof hex !== 'string') return null
    let value = hex.trim().replace(/^#/, '')
    if(value.length === 3) value = value.split('').map(c => c + c).join('')
    if(!/^[0-9a-f]{6}$/i.test(value)) return null

    const int = parseInt(value, 16)
    return {r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255}
}

// Perceived brightness on a 0–255 scale (same weighting the asset grid already uses).
const perceivedBrightness = hex => {
    const rgb = parseHexColor(hex)
    if(!rgb) return null
    return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b
}

// Treat anything at or below mid-grey as a dark surface; unknown values are assumed light
// (the historical default grid color is white).
const isDarkColor = hex => {
    const brightness = perceivedBrightness(hex)
    return brightness !== null && brightness < 140
}

// Solid text colors that read well on the given surface regardless of the active theme.
const contrastTextColor = (hex, {dark = '#ddd', light = 'rgba(0, 0, 0, 0.85)'} = {}) =>
    isDarkColor(hex) ? dark : light

// WCAG relative luminance and contrast ratio for {r,g,b} colors.
const relativeLuminance = ({r, g, b}) => {
    const channel = v => {
        v /= 255
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
    }
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

const contrastRatio = (a, b) => {
    const la = relativeLuminance(a), lb = relativeLuminance(b)
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

const WCAG_AA_TEXT = 4.5

// Text de-emphasized toward mid-grey over a surface, but never past WCAG AA for body text.
// `amount` is how far toward grey to go (0 = full contrast text, 1 = mid-grey); the grid's
// captions use ~1. On light grids that stays legible; on dark grids grey drops below AA, so the
// amount is walked back until the ratio holds.
const mutedTextColor = (surfaceHex, amount = 0, minRatio = WCAG_AA_TEXT) => {
    const surface = parseHexColor(surfaceHex) || {r: 255, g: 255, b: 255}
    const base = isDarkColor(surfaceHex) ? 255 : 0
    const grey = 128

    const mix = t => {
        const v = Math.round(base + (grey - base) * t)
        return {r: v, g: v, b: v}
    }

    let t = Math.max(0, Math.min(1, amount))
    let color = mix(t)
    while(t > 0 && contrastRatio(color, surface) < minRatio) {
        t = Math.max(0, t - 0.05)
        color = mix(t)
    }

    return `rgb(${color.r}, ${color.g}, ${color.b})`
}

export {parseHexColor, perceivedBrightness, isDarkColor, contrastTextColor, relativeLuminance, contrastRatio, mutedTextColor}
